import { transition, trigger } from '@angular/animations';
import { AsyncPipe, NgFor, NgIf, isPlatformBrowser, isPlatformServer } from '@angular/common';
import {
  Component,
  ElementRef,
  HostListener,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, NavigationEnd, NavigationStart, Router, RouterLink } from '@angular/router';
import {
  ActivityDto,
  AssetColorLabel,
  AssetDto,
  AssetStatus,
  GalleryAccessLevel,
  GalleryColorLabelDto,
  GalleryDto,
  GalleryFeedbackOption,
  SectionDto,
  UserDto,
} from '@picflow/types';
import { Subscription } from 'rxjs';
import { ShareAssetDialogComponent } from 'src/app/account/dialogs/share-asset-dialog/share-asset-dialog.component';
import { ShareGalleryDialogComponent } from 'src/app/account/dialogs/share-gallery-dialog/share-gallery-dialog.component';
import { ShareSubGalleryDialogComponent } from 'src/app/account/dialogs/share-sub-gallery-dialog/share-sub-gallery-dialog.component';
import { fadeDownIf } from 'src/app/animations/fade-down.if.animation';
import { fadeUpIf } from 'src/app/animations/fade-up.if.animation';
import { moveRightIf } from 'src/app/animations/move-right.if.animation';
import { widthChangeState } from 'src/app/animations/width-change-state';
import { Rect } from 'src/app/classes/rect.class';
import { ScreenSizes } from 'src/app/classes/screen-sizes.class';
import { defaultColorLabels } from 'src/app/configs/standard-presets';
import {
  assetLoadSettings,
  doubleClickTime,
  dragScrollThreshold,
  maxSizeBeforeZip,
  maxZipRequestSize,
} from 'src/app/consts';
import { DeleteAssetsDialogComponent } from 'src/app/gallery/dialogs/delete-assets-dialog/delete-assets-dialog.component';
import { GallerySettingsDialogComponent } from 'src/app/gallery/dialogs/gallery-settings-dialog/gallery-settings-dialog.component';
import { ManualConfirmDialogComponent } from 'src/app/gallery/dialogs/manual-confirm-dialog/manual-confirm-dialog.component';
import { sortAssetSection } from 'src/app/gallery/lib/sort-asset-section';
import { ArchiveSize, GALLERY_DOWNLOAD_SIZE } from 'src/app/interfaces/archive-request.interface';
import { arraysEqual } from 'src/app/lib/arrays-equal';
import { changeCoverAsset } from 'src/app/lib/change-cover-asset';
import { convertGalleryDownloadSize } from 'src/app/lib/convert-gallery-download-size';
import { copy } from 'src/app/lib/copy';
import { getAllRouteData } from 'src/app/lib/get-all-route-data';
import {
  getAllowedAndEnabledFeedbackOptions,
  getAllowedFeedbackOptions,
  getPermissions,
} from 'src/app/lib/get-allowed-feedback-options';
import { getScrollbarWidth } from 'src/app/lib/get-scrollbar-width';
import { isMetaKey } from 'src/app/lib/is-meta-key';
import { simplifiedAssetNames } from 'src/app/lib/simplified-asset-names';
import { sleep } from 'src/app/lib/sleep';
import { ActivityService } from 'src/app/services/activity.service';
import { AnalyticsService } from 'src/app/services/analytics.service';
import { ArchiveService } from 'src/app/services/archive.service';
import { AssetFeedbackService } from 'src/app/services/asset-feedback.service';
import { AssetPreGenerateService } from 'src/app/services/asset-pre-generate.service';
import { AssetService } from 'src/app/services/asset.service';
import { BrowserService } from 'src/app/services/browser.service';
import { ContextMenuService } from 'src/app/services/context-menu.service';
import { DialogService } from 'src/app/services/dialog.service';
import { FeatureFlagsService } from 'src/app/services/feature-flags.service';
import { GalleryService } from 'src/app/services/gallery.service';
import { PermissionService } from 'src/app/services/permission.service';
import { SectionService } from 'src/app/services/section.service';
import { StateService } from 'src/app/services/state.service';
import { StorageService } from 'src/app/services/storage.service';
import { TenantService } from 'src/app/services/tenant.service';
import { ToastService } from 'src/app/services/toast.service';
import { UpdateService } from 'src/app/services/update.service';
import { UploadService } from 'src/app/services/upload.service';
import { WatcherService } from 'src/app/services/watcher.service';
import { ActivityBadgeComponent } from '../../../shared-extra/components/activity-badge/activity-badge.component';
import { DropComponent } from '../../../shared-extra/components/drop/drop.component';
import { GalleryDownloadMenuOptionsComponent } from '../../../shared-extra/components/gallery-download-menu-options/gallery-download-menu-options.component';
import { ProTagComponent } from '../../../shared-extra/components/pro-tag/pro-tag.component';
import { PermissionsPipe } from '../../../shared-extra/pipes/permissions.pipe';
import { ButtonMenuItemComponent } from '../../../shared/components/button-menu-item/button-menu-item.component';
import { ContextMenuComponent } from '../../../shared/components/context-menu/context-menu.component';
import { LoadingIndicatorComponent } from '../../../shared/components/loading-indicator/loading-indicator.component';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { ButtonDirective } from '../../../shared/directives/button.directive';
import { InferredHoverDirective } from '../../../shared/directives/inferred-hover.directive';
import { ResizeDirective } from '../../../shared/directives/resize.directive';
import { TooltipDirective } from '../../../shared/directives/tooltip.directive';
import { AssetUrlPipe } from '../../../shared/pipes/asset-url.pipe';
import { AssetVersionUrlPipe } from '../../../shared/pipes/asset-version-url.pipe';
import { TranslateLabelPipe } from '../../../shared/pipes/translate-label.pipe';
import { TranslatePipe } from '../../../shared/pipes/translate.pipe';
import { DownloadAllDialogComponent } from '../../dialogs/download-all-dialog/download-all-dialog.component';
import { ExportGalleryDialogComponent } from '../../dialogs/export-gallery-dialog/export-gallery-dialog.component';
import { MoveAssetsDialogComponent } from '../../dialogs/move-assets-dialog/move-assets-dialog.component';
import { Filter } from '../../interfaces/filter.interface';
import { filterAssets } from '../../lib/filter-assets';
import { allowedAssetSortingAndGroupingValues, getAssetGroups } from '../../lib/sort-assets-sorting-and-grouping';
import { CanDisplayAssetPipe } from '../../pipes/can-display-asset.pipe';
import { AssetGroup } from '../../types/asset-group.type';
import { AdvancedModeContentHeaderComponent } from '../advanced-mode-content-header/advanced-mode-content-header.component';
import { AdvancedModeSidebarComponent } from '../advanced-mode-sidebar/advanced-mode-sidebar.component';
import { AssetContextMenuComponent } from '../asset-context-menu/asset-context-menu.component';
import { VideoThumbnailComponent } from '../video-thumbnail/video-thumbnail.component';
import { getNewAsset } from './get-new-asset';

@Component({
  selector: 'app-advanced-mode',
  templateUrl: './advanced-mode.component.html',
  styleUrls: ['./advanced-mode.component.scss'],
  animations: [
    trigger('emptyParent', [transition(':enter', [])]),
    trigger('sidebar', moveRightIf),
    trigger('content', widthChangeState('100%', 'calc(100% - 300px)')),
    trigger('selectedAssetsActions', fadeUpIf),
    trigger('assetActionsTop', fadeDownIf),
    trigger('assetActionsBottom', fadeUpIf),
  ],
  standalone: true,
  imports: [
    NgIf,
    AdvancedModeSidebarComponent,
    AdvancedModeContentHeaderComponent,
    DropComponent,
    ButtonDirective,
    SvgComponent,
    NgFor,
    FormsModule,
    InferredHoverDirective,
    RouterLink,
    ActivityBadgeComponent,
    ResizeDirective,
    ContextMenuComponent,
    AssetContextMenuComponent,
    ButtonMenuItemComponent,
    GalleryDownloadMenuOptionsComponent,
    TooltipDirective,
    LoadingIndicatorComponent,
    ProTagComponent,
    AssetUrlPipe,
    AssetVersionUrlPipe,
    PermissionsPipe,
    TranslatePipe,
    TranslateLabelPipe,
    AsyncPipe,
    VideoThumbnailComponent,
    CanDisplayAssetPipe,
  ],
})
export class AdvancedModeComponent implements OnInit, OnChanges, OnDestroy {
  @Input() gallery: GalleryDto;
  @Input() sections: SectionDto[];
  @Input() clientMode = false;
  @Input('assets') _assets: AssetDto[];
  @Input() blockShortcuts: boolean;
  accessLevel: GalleryAccessLevel;
  assets: AssetDto[] = [];
  assetGroups: AssetGroup[] = [];
  sortingAndGrouping = 'auto';
  showSidebar = true;
  showAssets = false;
  columnCount: number;
  cellSize: number;
  assetWidth: string;
  defaultAssetSize = 340;
  assetTargetSize = this.defaultAssetSize;
  assetMinSize = 180;
  assetMaxSize = 500;
  selectedAssets: Map<string, boolean> = new Map();
  filters: Filter[] = [];
  searchTerm: string;
  searchTermExact: boolean;
  selectedAssetsCount = 0;
  firstSelected: AssetDto;
  lastSelected: AssetDto;
  lastClicked: AssetDto;
  firstSelectedGroup: AssetGroup;
  selectedCombined: { status: string; color_label: string; is_liked: boolean };
  simplifiedAssetNames: { [assetId: string]: string } = {};
  inView: any = {};
  uploadEl: ElementRef;
  totalSize = 0;
  lastClickTime = 0;
  uploadingCount = 0;
  multiselectMode = false;
  assetTouchStartPos: Rect;
  multiTouchTimeout: any;
  potentialDragging: AssetDto;
  potentialDraggingStartPos: Rect;
  mousePos: Rect;
  scrollingPage: number;
  isMobile: boolean;
  isDesktop: boolean;
  dragging: { index: number; groupIndex: number; pos?: Rect } = undefined;
  hoverAsset: AssetDto;
  contextOpenAsset: AssetDto;
  updateSub: Subscription;
  routerSub: Subscription;
  assetSub: Subscription;
  galleryUpdateSub: Subscription;
  browserPlatform: boolean;
  selectedGroups: { [key: string]: boolean } = {};
  downloadingSelected = false;
  notifiedAboutSorting = false;
  colorLabels: GalleryColorLabelDto[] = [];
  groupings = allowedAssetSortingAndGroupingValues;
  downloadSizes: ArchiveSize[] = ['original', 'large', 'medium', 'small'];
  allowedFeedbackOptions: GalleryFeedbackOption[] = [];
  allowedAndEnabledFeedbackOptions: GalleryFeedbackOption[] = [];
  isGalleryGuest: boolean;
  assetPage: boolean;
  people: { userOrGuest: UserDto; assetIds: string[]; type: 'user' | 'guest' }[];
  hoverAssetOffsetX = 0;

  @ViewChild('sidebar') sidebar: AdvancedModeSidebarComponent;
  @ViewChild('header') header: AdvancedModeContentHeaderComponent;
  @ViewChild('content') content: ElementRef;
  @ViewChild('assetsContainer') assetsContainer: ElementRef<HTMLDivElement>;
  @HostListener('window:resize', ['$event']) winResize(event: Event) {
    if (!this.gallery) return;
    this.calculateColumnCount();
    this.calcInView();
    this.isMobile = ScreenSizes.screenSize() === 'mobile';
    this.isDesktop = ScreenSizes.screenSize() === 'desktop';
  }
  @HostListener('window:scroll', ['$event']) docScroll(event: Event) {
    this.calcInView();
  }
  @HostListener('window:keydown', ['$event'])
  keyEvent(event: KeyboardEvent) {
    if (this.blockShortcuts) return;
    if (this.dialog.dialogOpen) return;
    this.keyDown(event);
  }

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private assetService: AssetService,
    private storage: StorageService,
    private state: StateService,
    private dialog: DialogService,
    private router: Router,
    private route: ActivatedRoute,
    private contextMenuService: ContextMenuService,
    private uploadService: UploadService,
    private sectionService: SectionService,
    private galleryService: GalleryService,
    private assetFeedbackService: AssetFeedbackService,
    private tenantService: TenantService,
    private archiveService: ArchiveService,
    private watcherService: WatcherService,
    private updateService: UpdateService,
    private toasts: ToastService,
    public featureFlags: FeatureFlagsService,
    private analytics: AnalyticsService,
    private browserService: BrowserService,
    private permissionService: PermissionService,
    private activityService: ActivityService,
    private versionUrlPipe: AssetVersionUrlPipe,
    private assetPreGenerateService: AssetPreGenerateService
  ) {
    this.browserPlatform = isPlatformBrowser(this.platformId);
    if (!this.browserPlatform) return;
    this.loadPreferences();
    this.accessLevel = getAllRouteData(this.route.snapshot).accessLevel;
    this.isGalleryGuest = getAllRouteData(this.route.snapshot).isGalleryGuest;

    if (ScreenSizes.screenSize() === 'mobile') {
      this.showSidebar = false;
      this.assetTargetSize = 150;
    }
    this.calculateColumnCount();
    this.isMobile = ScreenSizes.screenSize() === 'mobile';
    this.isDesktop = ScreenSizes.screenSize() === 'desktop';

    this.routerSub = this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        this.assetPage = this.browserService.urlIsAssetUrl(event.url);
      }
      if (event instanceof NavigationEnd) {
        this.accessLevel = getAllRouteData(this.route.snapshot).accessLevel;
      }
    });

    this.galleryUpdateSub = this.galleryService.onUpdates.subscribe(event => {
      if (!event) return;
      if (!this.gallery) return;
      if (event[0] !== 'updated') return;
      if (event[1] !== this.gallery.id) return;
      this.calcAllowedFeedbackOptions();
    });

    this.assetSub = this.assetService.onUpdates.subscribe(event => {
      if (!event) return;
      if (!this._assets) return;
      const asset = this._assets.find(asset => asset.id === event[0]);
      if (!asset) return;
      if (event[1] === 'asset_moved') {
        this.onMovedOut(asset);
      }
    });

    this.updateSub = this.updateService.onAssetUpdate.subscribe(event => {
      if (!event) return;
      if (!this._assets) return;
      if (event.name === 'asset.created') {
        if (this._assets.find(img => img.id === event.asset.id)) return;
        this._assets.push(event.asset);
        this.calcSimplifiedAssetNames();
      }
      if (event.name === 'asset.deleted') {
        const index = this._assets.findIndex(img => img.id === event.asset.id);
        if (index !== -1) {
          this._assets.splice(index, 1);
          this.calcPeople();
        }
      }
      if (event.name === 'asset.updated') {
        const index = this._assets.findIndex(img => img.id === event.asset.id);
        if (index !== -1) {
          Object.assign(this._assets[index], event.asset);
          this.calcPeople();
        }
      }

      this.filterChange();
      this.calcSelectedCombined();
    });
  }

  ngOnInit() {
    if (!this.browserPlatform) return;
    this.handleFiltersFromQuery();
  }

  private handleFiltersFromQuery() {
    const filterString = this.route.snapshot.queryParams.filters;
    if (filterString) {
      let filters: Filter[];
      try {
        filters = JSON.parse(filterString);
      } catch (err) {
        console.warn('Not able to parse filters from url');
        console.error(err);
      }
      if (filters) {
        this.filters = filters;
        this.filterChange();
      }
    }
  }

  async ngOnChanges(changes: SimpleChanges) {
    if (isPlatformServer(this.platformId)) return;
    if (changes.gallery) {
      this.unselectAll();
      this.setupSelectedAssets();
      this.calcSimplifiedAssetNames();
      this.filterChange();
      this.showAssets = true;
      this.calcAllowedFeedbackOptions();
      this.calcPeople();
    }
    if (changes._assets) this.filterChange();
  }

  private async calcPeople() {
    const activities = await this.getAllActivitiesFiltered();
    if (!this.people) this.people = [];
    this.people.length = 0;
    activities.forEach(activity => {
      if (activity.type === 'asset-like.created') {
        const asset = this._assets.find(img => img.id === activity.context_asset);
        if (asset) {
          if (asset.likes === 0) {
            return;
          }
          if (asset.likes === 1) {
            const isCurrentUser = this.state.user && this.state.user.id === activity.user?.['id'];

            if (isCurrentUser && !asset['is_liked']) {
              return;
            }

            if (!isCurrentUser && asset['is_liked']) {
              return;
            }
          }
        }
      }

      if (activity.type === 'comment.created') {
        const asset = this._assets.find(img => img.id === activity.context_asset);
        if (asset && asset.text_comments_total_count === 0) return;
      }

      if (activity.type === 'asset-annotation.created') {
        const asset = this._assets.find(img => img.id === activity.context_asset);
        if (asset && asset.annotation_comments_total_count === 0) return;
      }

      if (activity.user) {
        if (!this.people.find(p => p.userOrGuest.id === activity.user['id'])) {
          this.people.push({ userOrGuest: <UserDto>(<any>activity.user), assetIds: [], type: 'user' });
        }
      }
      if (activity.guest) {
        if (!this.people.find(p => p.userOrGuest.id === activity.guest['id'])) {
          this.people.push({ userOrGuest: <UserDto>(<any>activity.guest), assetIds: [], type: 'guest' });
        }
      }

      const person = this.people.find(p => {
        if (activity.user) return p.userOrGuest.id === activity.user?.['id'];
        if (activity.guest) return p.userOrGuest.id === activity.guest?.['id'];
      });

      if (person) {
        person.assetIds.push(<string>activity.context_asset);
      }
    });

    await this.filterChange();
    this.sidebar?.calcAssetCounts();
  }

  private calcAllowedFeedbackOptions() {
    this.allowedAndEnabledFeedbackOptions = getAllowedAndEnabledFeedbackOptions(
      this.gallery,
      this.state.plan,
      this.accessLevel
    );
    if (this.accessLevel !== 'edit') {
      // in client mode we dont even want to show non enabled feedback options (in advanced mode sidebar and favourites)
      this.allowedFeedbackOptions = getAllowedAndEnabledFeedbackOptions(
        this.gallery,
        this.state.plan,
        this.accessLevel
      );
    } else {
      this.allowedFeedbackOptions = getAllowedFeedbackOptions(this.gallery, this.state.plan, this.accessLevel);
    }
    const permissions = getPermissions(this.gallery, this.state.plan);
    this.colorLabels = this.gallery.color_labels;
    if (!permissions.asset.customLabels) this.colorLabels = defaultColorLabels;
  }

  private setupSelectedAssets() {
    this.selectedAssets = new Map();
    this._assets.forEach(img => this.selectedAssets.set(img.id, false));
  }

  private calcSimplifiedAssetNames() {
    this.simplifiedAssetNames = simplifiedAssetNames(this._assets);
  }

  stopPropagation(event: Event) {
    this.contextMenuService.closeContext();
    event.stopPropagation();
  }

  resetFilters() {
    this.filters = [];
    this.header.setSearch('');
    this.filterChange();
    this.sidebar?.resetFilters();
    this.setAdvancedModeAssets();
  }

  search(value: string, exact: boolean = false) {
    this.searchTerm = value;
    this.searchTermExact = exact;
    this.filterChange();
  }

  async onSideMenuToggle() {
    this.showSidebar = !this.showSidebar;
    this.savePreferences();
    await sleep(100);
    this.calculateColumnCount();
    this.calcInView();
  }

  calculateColumnCount() {
    if (isPlatformServer(this.platformId)) return 8;
    let width = window.innerWidth - getScrollbarWidth();
    if (this.showSidebar) width -= 300;
    this.columnCount = Math.ceil(width / this.assetTargetSize);
    this.assetWidth = `calc(${100 / this.columnCount}% + 1px)`;
    this.cellSize = width / this.columnCount;
  }

  async filterChange() {
    if (this.filters.length) {
      this.state.activeFilterIncludesYourFavorites = !!this.filters.find(f => f.key === 'is_liked');
      this.state.activeFilterString = JSON.stringify(
        this.filters.filter(f => f.key !== 'is_liked').map(f => [f.key, f.values])
      );
    } else this.state.activeFilterString = undefined;

    this.assets = filterAssets(this._assets, this.filters, this.searchTerm, this.searchTermExact, this.people);

    if (this.sortingAndGrouping.endsWith('asset_name') || this.sortingAndGrouping.endsWith('created_at')) {
      this.assets = sortAssetSection(this.assets, this.sortingAndGrouping);
    } else {
      // sort like in designer
      const newAssets = [];
      this.sections.forEach(section => {
        const assets = this.assets.filter(asset => asset.section === section.id);
        const sortedAssets = sortAssetSection(assets, section.sorting);
        newAssets.push(...sortedAssets);
      });
      this.assets = newAssets;
    }

    this.setAdvancedModeAssets();

    this.setGroupedAssets();
    await sleep(0);
    this.calcInView();
  }

  isGrouping() {
    return this.groupings.includes(this.sortingAndGrouping);
  }

  getGroupedAssets(): AssetGroup[] {
    return getAssetGroups(this.assets, this.sortingAndGrouping, this.sections, this.colorLabels);
  }

  setGroupedAssets() {
    const groups = this.getGroupedAssets();
    if (JSON.stringify(groups) !== JSON.stringify(this.assetGroups)) {
      this.assetGroups = groups;
    }
  }

  changeAssetSize(size: number) {
    this.assetTargetSize = size;
    this.savePreferences();
    this.calculateColumnCount();
    this.calcInView();
  }

  changeSortingAndGrouping(sortingAndGrouping: string) {
    this.sortingAndGrouping = sortingAndGrouping;
    this.filterChange();
    this.savePreferences();
  }

  private checkAssetInView() {
    if (!this.lastSelected) return;
    const el = document.getElementById('asset-' + this.lastSelected.id);
    if (!el) return;
    const bounds = el.getBoundingClientRect();
    const contentBounds = this.content.nativeElement.getBoundingClientRect();
    if (bounds.top < contentBounds.top) {
      this.content.nativeElement.scrollTop += bounds.top - contentBounds.top - 20;
    } else if (bounds.bottom > window.innerHeight - 20) {
      const bottom = bounds.bottom - contentBounds.height;
      this.content.nativeElement.scrollTop += bottom - contentBounds.top + 20;
    }
  }

  private unselectAssets(assets: AssetDto[]) {
    assets.forEach(asset => {
      if (!this.selectedAssets.get(asset.id)) return;
      this.selectedAssets.set(asset.id, false);
      this.selectedAssetsCount -= 1;
    });
    if (!this.getSelectedAssets().length) {
      this.firstSelected = undefined;
      this.lastSelected = undefined;
    }
    this.calcTotalSize();
    this.calcSelectedCombined();
    this.checkGroups();

    this.setAdvancedModeAssets();
  }

  unselectAsset = (asset: AssetDto) => this.unselectAssets([asset]);
  unselectAll = () => {
    this.unselectAssets(this._assets);
    this.selectedAssetsCount = 0;
    this.multiselectMode = false;
    if (this.dragging) {
      this.dragging = undefined;
      this.potentialDragging = undefined;
    }
  };

  private selectAssets(assets: AssetDto[]) {
    const selectedAssets = this.getSelectedAssets();
    if (!selectedAssets.length) this.firstSelected = assets[0];
    this.lastSelected = assets.slice().pop();
    this.lastClicked = this.lastSelected;
    assets.forEach(asset => {
      if (this.selectedAssets.get(asset.id)) return;
      this.selectedAssets.set(asset.id, true);
      this.selectedAssetsCount += 1;
    });
    this.calcTotalSize();
    this.calcSelectedCombined();
    this.checkAssetInView();
    this.checkGroups();
    this.setAdvancedModeAssets();
  }

  selectAsset = (asset: AssetDto) => this.selectAssets([asset]);
  selectAll = () => this.selectAssets(this.assets);

  assetClick(asset: AssetDto, event: MouseEvent) {
    if (this.dragging) return;
    const metaKey = isMetaKey(event);
    if (metaKey || this.multiselectMode) {
      if (this.selectedAssets.get(asset.id)) {
        this.unselectAsset(asset);
        if (!this.selectedAssetsCount) this.multiselectMode = false;
      } else this.selectAsset(asset);
    } else if (event.shiftKey && this.firstSelected) {
      const _assets = [].concat(...this.assetGroups.map(group => group.assets));

      let range = [
        _assets.findIndex(img => img.id === this.firstSelected.id),
        _assets.findIndex(img => img.id === asset.id),
      ];
      if (range[0] > range[1]) range.reverse();

      const assets = _assets.filter((img, i) => i >= range[0] && i <= range[1]);

      this.unselectAll();
      this.selectAssets(assets);
    } else {
      if (Date.now() - this.lastClickTime < doubleClickTime) {
        if (this.lastClicked?.id === asset.id) this.openAsset(asset);
      }
      this.lastClickTime = Date.now();
      if (this.selectedAssetsCount === 1 && this.selectedAssets.get(asset.id)) {
        this.unselectAll();
      } else {
        this.unselectAll();
        this.selectAsset(asset);
      }
    }
  }

  private calcSelectedCombined() {
    const selectedAssets = this.getSelectedAssets();
    if (!selectedAssets.length) return;

    this.selectedCombined = {
      is_liked: selectedAssets[0]['is_liked'],
      status: selectedAssets[0].status,
      color_label: selectedAssets[0].color_label,
    };

    for (let key in this.selectedCombined) {
      if (selectedAssets.find(img => img[key] !== this.selectedCombined[key])) {
        this.selectedCombined[key] = undefined;
      }
    }
  }

  private calcTotalSize() {
    this.totalSize = 0;
    this.assets.forEach(img => {
      if (!this.selectedAssets.get(img.id)) return;
      this.totalSize += img.version_data.size;
    });
  }

  async downloadSelected(size: ArchiveSize) {
    if (this.downloadingSelected) return;
    this.downloadingSelected = true;

    const assets = this.getSelectedAssets();

    if (assets.length <= maxSizeBeforeZip) {
      await this.assetService.downloadAssetVersions(
        assets.map(asset => asset.version_data),
        assets[0].type,
        assets.map(asset => asset.watermark_enabled),
        this.gallery.id,
        GALLERY_DOWNLOAD_SIZE[size]
      );
      this.downloadingSelected = false;
      return;
    }

    const galleryData = {
      gallery: this.gallery,
      assets,
    };

    const { requests, totalDownloadSize } = await this.archiveService.createGalleryArchivesWithAssets(
      [galleryData],
      size,
      false
    );

    if (totalDownloadSize > maxZipRequestSize) {
      const resp = await this.dialog.open(DownloadAllDialogComponent, {
        totalKb: totalDownloadSize / 1024,
        amAllowed: this.gallery.client_advanced_mode_enabled,
        galleries: [this.gallery],
      });
      this.downloadingSelected = false;
      if (!resp) return;
      if (resp === 'am') return this.router.navigate(['./advanced'], { relativeTo: this.route });
      this.downloadingSelected = true;
    }

    for (let i in requests) {
      const request = requests[i];
      const archive = await this.archiveService.requestArchiveCreation(
        request.request,
        this.gallery.id,
        request.assetCount,
        totalDownloadSize,
        requests.length === 1 ? undefined : { index: parseInt(i), total: requests.length }
      );
      await this.archiveService.onArchiveDone(archive);
    }

    this.downloadingSelected = false;
  }

  getDownloadSizes(): ArchiveSize[] {
    const assets = Array.from(this.selectedAssets.entries())
      .filter(([_id, v]) => v)
      .map(([id]) => this.assets.find(img => img.id === id));

    const maxShortEdge = this.archiveService.getMaxShortEdge(assets);
    const sizes: ArchiveSize[] = [];
    const enabledSizes = convertGalleryDownloadSize(this.gallery.download_media_size);

    this.downloadSizes.forEach(size => {
      if (maxShortEdge < GALLERY_DOWNLOAD_SIZE[size]) return;
      if (!enabledSizes[size]) return;
      sizes.push(size);
    });

    return sizes;
  }

  async deleteSelectedAssets() {
    const assetsToDelete = Array.from(this.selectedAssets.entries())
      .filter(a => a[1])
      .map(item => this.assets.find(img => img.id === item[0]))
      .filter(a => a);

    if (!assetsToDelete.length) return;

    const resp = await this.dialog.open(DeleteAssetsDialogComponent, { assetCount: assetsToDelete.length });
    if (!resp) return;

    // Deleting assets
    await Promise.all(assetsToDelete.map(img => this.assetService.deleteAsset(img)));

    // Removing deleted assets
    this._assets = this._assets.filter(img => !assetsToDelete.find(_img => img.id === _img.id));

    // Updating other assets
    // for (let asset of assetsToDelete) {
    //   await this.assetService.handleAssetOrderChange(asset);
    // }
    this.unselectAll();
    this.filterChange();
    this.setupSelectedAssets();
    this.calcTotalSize();
    this.calcSelectedCombined();
    this.analytics.track('assets_deleted', true);
  }

  async onDelete(img: AssetDto) {
    // await this.assetService.deleteAsset(img);
    this.unselectAsset(img);
    this._assets = this._assets.filter(_img => _img.id !== img.id);
    // const section = await this.sectionService.getSection(<string>img.section, <string>img.gallery);
    // if (section.sorting === 'manual') {
    //   this.assetService.handleAssetOrderChange(img);
    // }
    this.filterChange();
    this.setupSelectedAssets();
    this.calcTotalSize();
    this.calcSelectedCombined();
    this.sidebar?.createDynamicFilters();
  }

  async onMovedOut(img: AssetDto) {
    this.unselectAsset(img);
    this._assets = this._assets.filter(_img => _img.id !== img.id);
    // this.assetService.handleAssetOrderChange(img);
    this.filterChange();
    this.setupSelectedAssets();
    this.calcTotalSize();
    this.calcSelectedCombined();
    this.sidebar?.createDynamicFilters();
  }

  onNewVersion() {
    this.calculateColumnCount();
    this.calcInView();
    this.filterChange();
    this.sidebar?.createDynamicFilters();
  }

  private loadPreferences() {
    if (!this.storage.has('advanced-mode-preferences')) return;
    let preferences = this.storage.get('advanced-mode-preferences');
    if (preferences.assetTargetSize) this.assetTargetSize = preferences.assetTargetSize;
    if (preferences.showSidebar !== undefined) this.showSidebar = preferences.showSidebar;
    if (preferences.sortingAndGrouping !== undefined) this.sortingAndGrouping = preferences.sortingAndGrouping;
  }

  private savePreferences() {
    this.storage.set('advanced-mode-preferences', {
      assetTargetSize: this.assetTargetSize,
      showSidebar: this.showSidebar,
      sortingAndGrouping: this.sortingAndGrouping,
    });
  }

  private async likeAssets(assets: AssetDto[]) {
    if (!this.gallery.enabled_feedback_options.includes('favourite')) return;
    if (!(await this.checkAuth())) return;

    assets.forEach(asset => {
      asset['is_liked'] = true;
      asset.likes += 1;
      this.watcherService.watchAsset(asset.id, this.gallery.id);
      this.assetService.likeAsset(asset.id, this.gallery.id);
    });
    this.calcSelectedCombined();
    this.filterChange();
  }

  private unlikeAssets(assets: AssetDto[]) {
    if (!this.gallery.enabled_feedback_options.includes('favourite')) return;
    assets.forEach(asset => {
      asset['is_liked'] = false;
      asset.likes -= 1;
      this.assetService.unlikeAsset(asset.id, this.gallery.id);
    });
    this.calcSelectedCombined();
    this.filterChange();
  }

  toggleAssetLike(asset: AssetDto) {
    if (asset['is_liked']) this.unlikeAssets([asset]);
    else this.likeAssets([asset]);
  }

  toggleLikeSelectedAssets() {
    if (!this.selectedCombined) return;
    let liked = this.selectedCombined.is_liked;
    if (liked === undefined || liked === false) {
      // If some assets are liked and some are not then we like them all
      this.likeSelectedAssets();
    } else if (liked === true) {
      this.unlikeSelectedAssets();
    }
  }

  private likeSelectedAssets() {
    const assets = this.getSelectedAssets().filter(img => !img['is_liked']);
    this.likeAssets(assets);
  }

  private unlikeSelectedAssets() {
    const assets = this.getSelectedAssets().filter(img => img['is_liked']);
    this.unlikeAssets(assets);
  }

  async updateAssetStatus(asset: AssetDto, status: AssetStatus) {
    if (!this.gallery.enabled_feedback_options.includes('flag')) return;
    if (asset.status === status) return;
    if (this.accessLevel !== 'edit' && !this.allowedFeedbackOptions.includes('flag')) return;
    if (!(await this.checkAuth())) return;
    asset.status = status;
    this.assetService.updateAsset(asset.id, { status }, this.gallery.id, <string>asset.section);
    this.watcherService.watchAsset(asset.id, this.gallery.id);
    this.filterChange();
    this.calcSelectedCombined();
  }

  async updateSelectedAssetStatus(status: AssetStatus) {
    const selectedAssets = this.getSelectedAssets();
    if (!selectedAssets.length) return;
    selectedAssets.forEach(img => this.updateAssetStatus(img, status));
  }

  async updateAssetColorLabel(asset: AssetDto, color_label: AssetColorLabel) {
    if (!this.gallery.enabled_feedback_options.includes('color_label')) return;
    if (asset.color_label === color_label) return;
    if (this.accessLevel !== 'edit' && !this.allowedFeedbackOptions.includes('color_label')) return;
    if (!(await this.checkAuth())) return;
    asset.color_label = color_label;
    this.assetService.updateAsset(asset.id, { color_label }, this.gallery.id, <string>asset.section);
    this.watcherService.watchAsset(asset.id, this.gallery.id);
    this.filterChange();
    this.calcSelectedCombined();
  }

  async updateSelectedAssetColorLabel(color_label: AssetColorLabel) {
    const selectedAssets = this.getSelectedAssets();
    selectedAssets.forEach(img => this.updateAssetColorLabel(img, color_label));
  }

  private openAsset(asset: AssetDto) {
    const filters = this.filters.length ? JSON.stringify(this.filters) : undefined;
    const search = this.searchTerm;

    this.router.navigate([asset.id], {
      relativeTo: this.route,
      preserveFragment: true,
      queryParams: { filters, search },
    });
  }

  openExportDialog(mode?: 'pdf' | 'csv' | 'filenames') {
    this.dialog.open(ExportGalleryDialogComponent, { gallery: this.gallery, mode });
  }

  private keyDown(event: KeyboardEvent) {
    if (event.target['classList'].contains('textarea')) return;
    if (event.target['tagName'] === 'INPUT') return;
    if (event.target['tagName'] === 'TEXTAREA') return;

    const metaKey = isMetaKey(event);
    // These events should work even when typing in a field
    if (event.key === '=' || event.key === '+') {
      if (metaKey) {
        event.preventDefault();
        let size = this.assetTargetSize + 50;
        if (size > this.assetMaxSize) size = this.assetMaxSize;
        this.changeAssetSize(size);
      }
    }
    if (event.key === '-' && metaKey) {
      event.preventDefault();
      let size = this.assetTargetSize - 50;
      if (size < this.assetMinSize) size = this.assetMinSize;
      this.changeAssetSize(size);
    }

    if (event.key === '0' && metaKey) {
      event.preventDefault();
      this.changeAssetSize(this.defaultAssetSize);
    }

    if (event.key === 'p' && metaKey) {
      event.preventDefault();
      this.openExportDialog('pdf');
    }

    if (event.key === 'c' && metaKey) {
      event.preventDefault();
      if (this.selectedAssetsCount) this.moveSelectedAssets('copy');
    }
    if (event.key === 'x' && metaKey) {
      event.preventDefault();
      if (this.selectedAssetsCount) this.moveSelectedAssets('move');
    }

    if (event.key === 'e' && metaKey) {
      event.preventDefault();
      this.openExportDialog();
    }

    if (event.key === 'Escape') {
      if (this.header.searchField.nativeElement === document.activeElement) {
        this.header.unfocusSearch();
        this.header.setSearch('');
      } else {
        if (this.selectedAssetsCount) {
          this.unselectAll();
          event.preventDefault();
        } else {
          if (this.accessLevel !== 'edit') {
            this.router.navigate(['../'], { relativeTo: this.route, preserveFragment: true });
          }
        }
      }
    }

    if (['Backspace', 'Delete'].includes(event.key)) {
      this.deleteSelectedAssets();
    }

    // These events should NOT work when typing in a field
    if (['INPUT', 'TEXTAREA', 'SELECT'].includes(event.target['tagName'])) return;

    if (event.code === 'Space' || event.key === 'Enter') {
      if (!this.firstSelected) return;
      this.openAsset(this.firstSelected);
      event.preventDefault();
    }

    if (event.code === 'KeyA') {
      if (metaKey) {
        this.selectAll();
      } else {
        if (this.selectedCombined) {
          if (this.selectedCombined.status !== 'approved') this.updateSelectedAssetStatus('approved');
          else this.updateSelectedAssetStatus('no_status');
        }
      }
      event.preventDefault();
    }
    if (event.code === 'KeyX') {
      if (this.selectedCombined) {
        if (this.selectedCombined.status !== 'rejected') this.updateSelectedAssetStatus('rejected');
        else this.updateSelectedAssetStatus('no_status');
      }
      event.preventDefault();
    }

    if (event.code === 'KeyF') {
      this.toggleLikeSelectedAssets();
      event.preventDefault();
    }

    if (event.code === 'Tab') {
      if (metaKey) {
        event.preventDefault();
        this.header.focusSearch();
      } else this.onSideMenuToggle();
    }

    if (!metaKey) {
      if (event.key === 'r') this.updateSelectedAssetColorLabel('red');
      if (event.key === 'o') this.updateSelectedAssetColorLabel('orange');
      if (event.key === 'g') this.updateSelectedAssetColorLabel('green');
      if (event.key === 'b') this.updateSelectedAssetColorLabel('blue');
      if (event.key === 'p') this.updateSelectedAssetColorLabel('purple');
      if (event.key === 'n') this.updateSelectedAssetColorLabel(null);
    }

    if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) {
      event.preventDefault();
      if (!this.lastSelected) return;

      const assets = [].concat(...this.assetGroups.map(g => g.assets));
      let newAsset = getNewAsset(assets, this.assetGroups, this.lastSelected, this.columnCount, event.key);
      if (!newAsset) return;

      if (event.shiftKey) {
        let _range = [
          assets.findIndex(img => img.id === this.firstSelected.id),
          assets.findIndex(img => img.id === newAsset.id),
        ];
        if (_range[0] > _range[1]) _range.reverse();
        const assetsToSelect = assets.filter((img, i) => i >= _range[0] && i <= _range[1]);
        this.unselectAll();
        this.selectAssets(assetsToSelect);
      } else {
        if (!metaKey) this.unselectAll();
        this.selectAsset(newAsset);
      }
    }
  }

  calcInView() {
    if (!this.assets) return;
    if (this.assetPage) return;

    if (this.content) {
      const width = this.content.nativeElement.getBoundingClientRect().width;
      this.cellSize = width / this.columnCount;
    }

    this.inView = {};
    const toPreGenerate = [];
    const offset = 100;
    const header = 64;

    const scrollPos = document.scrollingElement.scrollTop;

    this.assetGroups.forEach((group, i) => {
      let groupTop = 0;
      const el = document.getElementById('group-' + i);
      if (el) groupTop = el.offsetTop;

      const groupHeader = this.isGrouping() ? 50 : 0;
      group.assets.forEach((img, i) => {
        const row = Math.floor(i / this.columnCount);
        const y = groupTop + groupHeader + header + row * this.cellSize;

        const top = y + this.cellSize + offset > scrollPos - this.cellSize;
        const bottom = y - offset < scrollPos + window.innerHeight + this.cellSize;

        const preload =
          top &&
          !bottom &&
          y - offset <
            scrollPos +
              window.innerHeight *
                (assetLoadSettings.galleryScreensToPreloadAfter + assetLoadSettings.galleryScreensToPreGenerate + 1) +
              this.cellSize;

        if (preload) {
          toPreGenerate.push(img);
        }

        this.inView[img.id] = top && bottom;
      });
    });

    toPreGenerate.forEach(async img => {
      const url = await this.versionUrlPipe.transform(
        img.version_data,
        img.version_data.uuid,
        img.version_data['status'],
        img.version_data.watermark_uuid,
        img.version_data.width > img.version_data.height ? this.cellSize * 1.5 : this.cellSize,
        true,
        img.watermark_enabled
      );
      this.assetPreGenerateService.preloadAsset(url);
    });
  }

  private getSelectedAssets() {
    return this._assets.filter(img => this.selectedAssets.get(img.id));
  }

  async uploadAssets(files: FileList) {
    if (this.accessLevel !== 'edit') return console.warn('Cant upload in client mode');
    let section = this.sections.find(s => s.object === 'asset');
    if (!section) {
      section = await this.sectionService.createSection(this.gallery.id, 'asset', this.sections.length);
      this.sections.push(section);
    }

    const assets = await this.assetService.getAssets(section.id, this.gallery.id);
    this.uploadingCount += 1;
    this.uploadService
      .managedUpload(files, this.gallery, section.id, assets, section.sorting)
      .subscribe(async next => {
        // if (next.type === 'started') {
        //   // this.setGroupedAssets();
        //   // this.calcInView();
        // }

        if (next.type === 'created') {
          if (this._assets.find(img => img.id === next.data.asset.id)) return; // avoiding race condition with websocket
          this._assets.push(next.data.asset);
          this.calcSimplifiedAssetNames();
          this.filterChange();
        }

        if (next.type === 'uploaded' && next.data.asset) {
          if (!this.gallery.teaser?.uuid) {
            const asset = await this.uploadService.waitForAssetProcessing(next.data.asset.id);
            if (!this.gallery.teaser?.uuid && this.assetService.canDisplayAsset(asset)) {
              this.gallery.teaser = { position: '50% 50%' };
              changeCoverAsset(this.gallery.teaser, asset);
              this.galleryService.updateGallery(this.gallery.id, { teaser: this.gallery.teaser });
            }
          }
        }
      })
      .add(() => {
        this.uploadingCount -= 1;
        // TODO: Fix this.. won't handle multiple uploads to multiple sections. Need to keep one uploading count per section
        if (!this.uploadingCount) {
          // const allAssets = await this.assetService.getAssets(section.id, this.gallery.id);
          const sectionAssets = this._assets.filter(img => img.section === section.id);
          const sortedAssets = sortAssetSection(sectionAssets, section.sorting);
          if (section.sorting === 'manual') {
            this.assetService.handleOrderChange(sortedAssets, this.gallery.id, section.id);
          }
        }
        this.tenantService.refreshTenant();
        this.calcSimplifiedAssetNames();
        this.sidebar?.createDynamicFilters();
      });
    this.filterChange();
  }

  uploadAssetEvent(event: any) {
    this.uploadAssets(event.files);
    if (this.uploadEl) this.uploadEl.nativeElement.value = '';
  }

  async addSubGallery() {
    const mainGalleryId: string = this.gallery['parent_gallery_id'] || this.gallery.id;
    const gallery = await this.galleryService.getGallery(mainGalleryId);
    await this.galleryService.createSubGallery(gallery);
    return gallery;
  }

  private async checkAuth() {
    return this.assetFeedbackService.checkAuth(this.gallery);
  }

  showSelectedActions() {
    if (!this.browserPlatform) return false;
    if (this.allowedAndEnabledFeedbackOptions.includes('favourite')) return true;
    if (this.allowedAndEnabledFeedbackOptions.includes('flag')) return true;
    if (this.allowedAndEnabledFeedbackOptions.includes('color_label')) return true;
    return false;
  }

  async moveSelectedAssets(mode?: 'copy' | 'move') {
    const assetsToMove = this.getSelectedAssets();

    const resp = await this.dialog.open(MoveAssetsDialogComponent, {
      assets: assetsToMove,
      gallery: this.gallery,
      mode,
    });
    if (resp === 'moved') {
      this._assets = this._assets.filter(img => !assetsToMove.find(_img => img.id === _img.id));
      this.unselectAll();
      this.filterChange();
    }
  }

  async toggleWatermarkSelectedAssets() {
    const assets = this.getSelectedAssets();
    const newValue = !assets[0].watermark_enabled;

    assets
      .filter(img => img.watermark_enabled !== newValue)
      .forEach(asset => {
        asset.watermark_enabled = newValue;
        this.assetService.updateAsset(
          asset.id,
          { watermark_enabled: newValue },
          this.gallery.id,
          <string>asset.section
        );
      });

    this.filterChange();
    this.calcSelectedCombined();
  }

  assetTouchStart(event: TouchEvent, asset: AssetDto) {
    if (this.multiselectMode) return;
    if (!event.touches[0]) return;
    this.assetTouchStartPos = Rect.fromTouch(event.touches[0]);
    this.multiTouchTimeout = setTimeout(() => {
      this.unselectAll();
      this.multiselectMode = true;
      this.selectAsset(asset);
    }, 700);
  }

  assetTouchMove(event: TouchEvent) {
    const dist = Rect.fromTouch(event.touches[0]).subtract(this.assetTouchStartPos).magnitude();
    if (dist < 10) return;
    clearTimeout(this.multiTouchTimeout);
  }

  assetTouchEnd() {
    clearTimeout(this.multiTouchTimeout);
  }

  assetMouseMove(event: MouseEvent) {
    const x = event.clientX;
    const box = event.target['getBoundingClientRect']();
    this.hoverAssetOffsetX = (x - box.x) / box.width;
  }

  onContextMenu(event: Event) {
    if (ScreenSizes.screenSize() === 'mobile') {
      event.preventDefault();
      return false;
    }
  }

  private getDragging(event: MouseEvent): { index: number; groupIndex: number; pos?: Rect } {
    let pos = Rect.fromMouseEvent(event);

    const groupEls = document.querySelectorAll('.asset-group');
    const groupEl = Array.from(groupEls).find(el => {
      const box = el.getBoundingClientRect();
      if (pos.y < box.y) return false;
      if (pos.y > box.y + box.height) return false;
      if (pos.x < box.x) return false;
      if (pos.x > box.x + box.width) return false;
      return true;
    });

    if (!groupEl) return { index: 0, groupIndex: 0 };

    const groupIndex = Array.from(groupEls).indexOf(groupEl);

    const assetEls = Array.from(groupEl.querySelectorAll('.asset-container-parent'));
    const el = assetEls.find(el => {
      const box = el.getBoundingClientRect();
      if (pos.y < box.y) return false;
      if (pos.y > box.y + box.height) return false;
      if (pos.x < box.x) return false;
      if (pos.x > box.x + box.width) return false;
      return true;
    });

    if (!el) {
      const lastAsset = assetEls[assetEls.length - 1];
      let index = 0;
      let _pos = Rect.fromDOMRect(groupEl.getBoundingClientRect());

      if (lastAsset) {
        const lastBox = lastAsset.getBoundingClientRect();
        _pos = Rect.fromDOMRect(lastBox);
        if (pos.x > lastBox.x) {
          index = assetEls.length;
          _pos.x += _pos.width;
        }
      }

      return { index, groupIndex, pos: _pos };
    }

    let index = assetEls.indexOf(el);

    let box = el.getBoundingClientRect();
    let percent = (1 / box.width) * (pos.x - box.x);
    if (percent > 0.5) {
      index += 1;
      box.x += box.width;
    }
    let _pos = Rect.fromDOMRect(box);

    return { index, groupIndex, pos: _pos };
  }

  startScroll(dir: number) {
    this.scrollingPage = dir;
    this.scroll(dir);
  }

  private async scroll(dir: number) {
    if (this.scrollingPage !== dir) return;
    document.scrollingElement.scrollTop += dir;
    await sleep(20);
    this.scroll(dir);
  }

  stopScroll() {
    this.scrollingPage = 0;
  }

  mouseDown(event: MouseEvent, asset: AssetDto) {
    event.preventDefault();
    if (event.shiftKey) return;
    if (isMetaKey(event)) return;
    if (this.accessLevel !== 'edit') return;
    this.potentialDragging = asset;
    this.potentialDraggingStartPos = Rect.fromMouseEvent(event);
  }

  mouseMove(event: MouseEvent) {
    if (event.buttons !== 1) {
      this.dragging = undefined;
      this.potentialDragging = undefined;
      return;
    }
    if (!this.potentialDragging) return;
    event.preventDefault();
    this.mousePos = Rect.fromMouseEvent(event);

    if (this.potentialDraggingStartPos.subtract(this.mousePos).magnitude() < 10) return;

    // const sorting = !this.isGrouping() && this.sortingAndGrouping;
    // if (sorting && this.sorting === sorting && sorting !== 'manual') this.sorting = 'manual'; // We need to switch to manual sorting

    if (this.sortingAndGrouping !== 'auto' && !this.notifiedAboutSorting) {
      this.notifiedAboutSorting = true;
      this.dragging = undefined;
      this.potentialDragging = undefined;
      return this.toasts.add('Dragging only works with "As Layout" sorting', 'info');
    }

    if (!this.dragging) {
      if (!this.selectedAssets.get(this.potentialDragging.id)) {
        this.unselectAll();
        this.selectAsset(this.potentialDragging);
      }
    }
    this.dragging = this.getDragging(event);

    this.stopScroll();
    if (this.mousePos.y - 64 < dragScrollThreshold) {
      this.startScroll(-3);
    } else if (window.innerHeight - this.mousePos.y < dragScrollThreshold) {
      this.startScroll(3);
    }
  }

  async handleSubGalleryDrop(event: MouseEvent) {
    // const path = getEventPath(event);
    // let moveToId = path.find(el => el.getAttribute('data-gallery-id'))?.getAttribute('data-gallery-id');
    // if (moveToId) {
    //   if (moveToId === 'new') {
    //     // this.sidebar.addingGallery = true;
    //     const gallery = await this.addSubGallery();
    //     // this.sidebar.addingGallery = false;
    //     moveToId = gallery.id;
    //   }
    //   await this.moveSelectedAssets(moveToId);
    // }
  }

  async mouseUp(event: MouseEvent) {
    this.notifiedAboutSorting = false;
    if (!this.dragging) return;
    this.handleSubGalleryDrop(event);

    if (!this.dragging.pos) return (this.dragging = undefined);

    event.preventDefault();

    const dragging: {
      index: number;
      groupIndex: number;
      pos?: Rect;
    } = copy(this.dragging);
    this.dragging = undefined;

    const assets = this.getSelectedItems();
    const assetSections = this.sections.filter(section => section.object === 'asset');
    const dropSection = assetSections[dragging.groupIndex];

    const dropGroup = this.assetGroups[dragging.groupIndex];
    const promises: any[] = [];

    const findTing = (index: number) => {
      if (index === -1) return -1;
      if (this.selectedAssets.get(dropGroup.assets[index].id)) return findTing(index - 1);
      return index;
    };
    const leftAsset = dropGroup.assets[findTing(dragging.index - 1)];

    const oldIndexes = assets.map(asset => {
      const group = this.assetGroups[assetSections.findIndex(s => s.id === asset.section)];
      return group.assets.findIndex(i => i.id === asset.id);
    });

    // Removing assets from old sections
    assets.forEach(asset => {
      const oldGroup = this.assetGroups[assetSections.findIndex(s => s.id === asset.section)];
      const oldIndex = oldGroup.assets.findIndex(i => i.id === asset.id);
      oldGroup.assets.splice(oldIndex, 1);
    });

    const newStartIndex = dropGroup.assets.indexOf(leftAsset) + 1;

    // Adding assets into position of new section
    assets.forEach((asset, i) => {
      const index = newStartIndex + i;
      dropGroup.assets.splice(index, 0, asset);
    });

    const newIndexes = assets.map(asset => {
      const group = this.assetGroups[assetSections.findIndex(s => s.id === asset.section)];
      return group.assets.findIndex(i => i.id === asset.id);
    });

    if (this.sortingAndGrouping !== 'auto') return; // should not happen

    const areChanges =
      !arraysEqual(oldIndexes, newIndexes) || dropGroup.assets.some(asset => asset.section !== dropSection.id);

    if (!areChanges) {
      this.filterChange();
      return;
    }

    if (dropSection.sorting !== 'manual') {
      const assetCount = await this.assetService.getAssetCount(this.gallery.id, dropSection.id);
      if (assetCount >= 100) {
        const resp = await this.dialog.open(ManualConfirmDialogComponent);
        if (!resp) {
          this.filterChange();
          return;
        }
      }
      // if the new section is empty, we don't need to set the sorting to manual
      if (assetCount > 0) {
        this.sectionService.updateSection(dropSection.id, { sorting: 'manual' });
        dropSection.sorting = 'manual';
        this.toasts.add('Asset order changed to Manual', 'info');
      }
    }

    dropGroup.assets.forEach(asset => {
      if (asset.section !== dropSection.id) {
        promises.push(
          this.assetService.updateAsset(asset.id, { section: dropSection.id }, this.gallery.id, <string>asset.section)
        );
        asset.section = dropSection.id;
      }
    });

    const manualChanges = await this.assetService.handleOrderChange(dropGroup.assets, this.gallery.id, dropSection.id);

    manualChanges.forEach(change => {
      const asset = this._assets.find(asset => asset.id === change.id);
      if (asset) asset.position = change.pos;
    });

    this.filterChange();

    // Await all assets to be updated
    await Promise.all(promises);

    // Waiting to avoid deadlock
    await sleep(50);

    // Await all sections to be fixed
    await Promise.all(
      assetSections.map(section => {
        if (section.sorting !== 'manual') return;
        return this.assetService.handleSectionAssetsOrderChange(section.id, this.gallery.id);
      })
    );
  }

  getSelectedItems(limit?: number) {
    const assetIds = Array.from(this.selectedAssets.entries())
      .filter(([k, v]) => v)
      .map(([k]) => k);
    const assets = this._assets.filter(img => assetIds.includes(img.id));
    if (!limit) return assets;
    return assets.slice(0, limit);
  }

  checkGroups() {
    this.assetGroups.forEach((group, i) => {
      this.selectedGroups['group-' + i] = !group.assets.find(img => !this.selectedAssets.get(img.id));
    });
  }

  handleSelectedGroupChange(group: AssetGroup, selected: boolean) {
    if (selected) this.selectAssets(group.assets);
    else this.unselectAssets(group.assets);
  }

  async setAsCover(asset: AssetDto) {
    changeCoverAsset(this.gallery.cover, asset);
    this.gallery.cover.position = '50% 50%';
    await this.galleryService.updateGallery(this.gallery.id, { cover: this.gallery.cover });
    this.toasts.add('Asset set as cover', 'success');
  }

  assetTrackFn(index: number, asset: AssetDto) {
    return asset.id;
  }

  openShareDialog(asset?: AssetDto) {
    if (asset) {
      this.dialog.open(ShareAssetDialogComponent, { gallery: this.gallery, asset });
    } else {
      if (this.gallery.section) {
        this.dialog.open(ShareSubGalleryDialogComponent, { gallery: this.gallery, asset });
      } else {
        this.dialog.open(ShareGalleryDialogComponent, { gallery: this.gallery, asset });
      }
    }
  }

  private setAdvancedModeAssets() {
    const assets: AssetDto[] = [];
    this.assetGroups.forEach(group => {
      group.assets.forEach(asset => {
        if (this.assets.find(i => i.id === asset.id)) assets.push(asset);
      });
    });
    this.state.advancedModeFilteredAssets = assets;
    this.state.advancedModeSelectedAssets = this.getSelectedItems();
  }

  openWatermarkingSettings() {
    if (this.permissionService.canDisplayElement('asset.watermarking', this.gallery)) {
      return this.dialog.open(GallerySettingsDialogComponent, { gallery: this.gallery });
    }
    const tenantId = this.gallery['tenant']?.id || this.gallery['tenant'];
    if (this.permissionService.shouldRedirectToPricingPage(tenantId)) {
      this.router.navigate(['/a', 'pricing']);
    }
  }

  private async getAllActivitiesFiltered() {
    const activities = await this.getAllActivities();
    const filtered: ActivityDto[] = [];

    activities.forEach(activity => {
      const activityPersonId = activity.user?.['id'] ?? activity.guest?.['id'];

      const duplicate = filtered.find(a => {
        if (a.context_asset !== activity.context_asset) return false;
        if (['asset-flag.created', 'asset-color-label.created', 'asset-status.created'].includes(activity.type)) {
          return a.type === activity.type;
        }

        const aPersonId = a.user?.['id'] ?? a.guest?.['id'];
        if (aPersonId !== activityPersonId) return false;
        return activity.type.split('.')[0] === a.type.split('.')[0];
      });
      if (duplicate) return;
      filtered.push(activity);
    });

    return filtered.filter(activity => {
      if (activity.type === 'asset-status.created') {
        return activity.data.object['status'] !== 'no_status';
      }
      if (activity.type === 'asset-color-label.created') {
        return activity.data.object['color_label'] !== null;
      }
      if (activity.type.split('.')[1] === 'deleted') {
        return false;
      }
      return true;
    });
  }

  private async getAllActivities(page = 0): Promise<ActivityDto[]> {
    const resp = await this.activityService.getGalleryFeedbackActivity(this.gallery.id, page, 100);
    if (resp.isMore) {
      const more = await this.getAllActivities(page + 1);
      return [...resp.activities, ...more];
    }
    return resp.activities;
  }

  ngOnDestroy() {
    this.state.activeFilterString = undefined;
    this.state.advancedModeFilteredAssets = undefined;
    this.state.advancedModeSelectedAssets = undefined;
    if (isPlatformServer(this.platformId)) return;
    this.updateSub?.unsubscribe();
    this.routerSub?.unsubscribe();
    this.galleryUpdateSub?.unsubscribe();
    this.assetSub?.unsubscribe();
  }
}
